// ═══════════════════════════════════════════════
// 文本翻译器: 中文原文 → 英文, 供页面级 DOM 翻译和图表翻译共用。
//
// 字典 key 是源码里的中文原文 (空白已规整), 三种匹配依次尝试:
//   1. 精确匹配
//   2. 模板匹配: key 里的 {0} {1} 是源码里的 ${...} / JSX {表达式},
//      运行时会被数字、名称等替换, 这里还原成正则去套
//   3. 分段兜底: 按 · : ; , 括号 等标点切开, 能翻的段翻掉, 其余保留
// 都不命中就原样返回 null —— 宁可留中文, 也不瞎翻。
// ═══════════════════════════════════════════════

const HAN = /[一-鿿]/;
const PLACEHOLDER = /\{(\d+)\}/g;
const SEGMENT_SPLIT = /(\s*[·•|｜/／:：;；,，、()（）[\]【】<>《》「」“”"→←—–=+\n]\s*)/;

type Template = { re: RegExp; en: string; needle: string };

export const normalizeText = (text: string) => text.replace(/\s+/g, ' ').trim();
export const hasHan = (text: string) => HAN.test(text);

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function compileTemplate(zh: string, en: string): Template | null {
  const parts = zh.split(PLACEHOLDER);
  // split 后奇数位是占位符序号
  let pattern = '^';
  let longest = '';
  parts.forEach((part, i) => {
    if (i % 2 === 1) {
      pattern += `(?<p${part}>[\\s\\S]*?)`;
    } else {
      const literal = part.trim();
      if (literal.length > longest.length) longest = literal;
      pattern += part.split(/\s+/).map(escapeRe).join('\\s*');
    }
  });
  if (!hasHan(longest) && !hasHan(zh.replace(PLACEHOLDER, ''))) return null;
  try {
    return { re: new RegExp(`${pattern}$`), en, needle: longest };
  } catch {
    return null;
  }
}

export type TextTranslator = {
  translate: (text: string) => string | null;
  size: number;
};

export function createTextTranslator(dict: Record<string, string>): TextTranslator {
  const exact = new Map<string, string>();
  const templates: Template[] = [];
  for (const [zh, en] of Object.entries(dict)) {
    if (typeof en !== 'string') continue;
    const key = normalizeText(zh);
    exact.set(key, en);
    if (/\{\d+\}/.test(key)) {
      const compiled = compileTemplate(key, en);
      if (compiled) templates.push(compiled);
    }
  }
  // 字面量越长的模板越具体, 优先匹配
  templates.sort((a, b) => b.needle.length - a.needle.length);

  const cache = new Map<string, string | null>();

  // 模板里捕获到的值本身也可能是一句可翻译的话 (嵌套模板), 递归一层
  const translateValue = (value: string, depth: number) => {
    const v = normalizeText(value);
    if (!hasHan(v)) return value;
    const lead = value.match(/^\s*/)?.[0] ?? '';
    const tail = value.match(/\s*$/)?.[0] ?? '';
    const hit = exact.get(v) ?? withTrailingMarks(v) ?? (depth < 2 ? viaTemplate(v, depth + 1) : null);
    return hit == null ? value : `${lead}${hit}${tail}`;
  };

  // 「支撑剂类型 *」「井名：」这类带必填星号/冒号的标签: 去掉尾巴后精确匹配
  const withTrailingMarks = (text: string): string | null => {
    const m = /^(.*?[^\s*＊:：])(\s*[*＊:：]+\s*)$/.exec(text);
    if (!m) return null;
    const hit = exact.get(m[1]);
    return hit == null ? null : `${hit}${m[2]}`;
  };

  const viaTemplate = (text: string, depth = 0): string | null => {
    for (const tpl of templates) {
      if (tpl.needle && !text.includes(tpl.needle)) continue;
      const m = tpl.re.exec(text);
      if (!m) continue;
      const groups = m.groups ?? {};
      return tpl.en.replace(PLACEHOLDER, (_, i: string) => translateValue(groups[`p${i}`] ?? '', depth));
    }
    return null;
  };

  const viaSegments = (text: string): string | null => {
    const pieces = text.split(SEGMENT_SPLIT);
    if (pieces.length < 2) return null;
    let changed = false;
    const out = pieces.map((piece) => {
      const trimmed = piece.trim();
      if (!hasHan(trimmed)) return piece;
      const hit = exact.get(trimmed) ?? withTrailingMarks(trimmed) ?? viaTemplate(trimmed);
      if (hit == null) return piece;
      changed = true;
      return piece.replace(trimmed, hit);
    });
    return changed ? out.join('') : null;
  };

  const translate = (raw: string): string | null => {
    if (!raw || !hasHan(raw)) return null;
    const text = normalizeText(raw);
    if (cache.has(text)) return cache.get(text) ?? null;
    const result = exact.get(text) ?? withTrailingMarks(text) ?? viaTemplate(text) ?? viaSegments(text);
    if (cache.size > 20000) cache.clear();
    cache.set(text, result);
    return result;
  };

  return { translate, size: exact.size };
}

/** 保留原文首尾空白, 只替换中间内容 —— 文本节点之间的空格常常是排版的一部分。 */
export function translatePreservingSpace(translator: TextTranslator, raw: string): string | null {
  const hit = translator.translate(raw);
  if (hit == null) return null;
  const lead = raw.match(/^\s*/)?.[0] ?? '';
  const tail = raw.match(/\s*$/)?.[0] ?? '';
  return `${lead}${hit}${tail}`;
}

/** 含 HTML 标签的字符串 (图表 tooltip formatter 返回值): 只翻标签外的文字。 */
export function translateHtmlString(translator: TextTranslator, html: string): string {
  if (!hasHan(html)) return html;
  return html.split(/(<[^>]*>)/).map((piece) => {
    if (piece.startsWith('<') || !hasHan(piece)) return piece;
    return translatePreservingSpace(translator, piece) ?? piece;
  }).join('');
}
